/**
 * Notification Rule Types
 *
 * Defines the rule engine types for the notification system.
 * Each rule type maps to a specific event lifecycle trigger.
 */

import { z } from "zod";

// ============================================================================
// Rule Type Enum
// ============================================================================

export const NOTIFICATION_RULE_TYPE = {
	UPCOMING_EVENT: "UPCOMING_EVENT",
	SAME_DAY: "SAME_DAY",
	EVENT_CONFIRMATION: "EVENT_CONFIRMATION",
	CANCELLATION_NOTICE: "CANCELLATION_NOTICE",
	EVENT_MODIFICATION: "EVENT_MODIFICATION",
	POST_EVENT_FOLLOWUP: "POST_EVENT_FOLLOWUP",
	NO_SHOW_FOLLOWUP: "NO_SHOW_FOLLOWUP",
	RE_ENGAGEMENT: "RE_ENGAGEMENT",
	CUSTOM: "CUSTOM",
} as const;

export type NotificationRuleType =
	(typeof NOTIFICATION_RULE_TYPE)[keyof typeof NOTIFICATION_RULE_TYPE];

// ============================================================================
// Delivery Channel Enum
// ============================================================================

/**
 * Delivery channels a notification rule (or reminder template) may target.
 * Mirrors the Prisma `NotificationChannel` enum and the domain
 * `NotificationChannel` type (`app-config.types.ts`), which serialize the
 * values verbatim (uppercase) both directions.
 */
export const NOTIFICATION_CHANNEL_VALUES = ["WHATSAPP", "EMAIL"] as const;

export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNEL_VALUES);

export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export const NotificationRuleTypeSchema = z.enum([
	"UPCOMING_EVENT",
	"SAME_DAY",
	"EVENT_CONFIRMATION",
	"CANCELLATION_NOTICE",
	"EVENT_MODIFICATION",
	"POST_EVENT_FOLLOWUP",
	"NO_SHOW_FOLLOWUP",
	"RE_ENGAGEMENT",
	"CUSTOM",
]);

/** Human-readable descriptions for each rule type */
export const NOTIFICATION_RULE_TYPE_LABELS: Record<
	NotificationRuleType,
	{ label: string; description: string }
> = {
	UPCOMING_EVENT: {
		label: "Upcoming Event",
		description: "Send a reminder N days before the event at a specific time",
	},
	SAME_DAY: {
		label: "Same Day",
		description: "Send a reminder N hours before the event on the same day",
	},
	EVENT_CONFIRMATION: {
		label: "Event Confirmation",
		description: "Send confirmation when an event is created or confirmed",
	},
	CANCELLATION_NOTICE: {
		label: "Cancellation Notice",
		description: "Send notice when an event is cancelled",
	},
	EVENT_MODIFICATION: {
		label: "Event Modified",
		description: "Send notice when an event is rescheduled or modified",
	},
	POST_EVENT_FOLLOWUP: {
		label: "Post-Event Follow-up",
		description: "Send follow-up N days after event completion",
	},
	NO_SHOW_FOLLOWUP: {
		label: "No-Show Follow-up",
		description: "Send follow-up after a user is marked as no-show",
	},
	RE_ENGAGEMENT: {
		label: "Re-Engagement",
		description: "Reach out to users who have been inactive for N days",
	},
	CUSTOM: {
		label: "Custom",
		description: "Send to specific contacts at a scheduled date & time",
	},
};

// ============================================================================
// Type-Specific Options Schemas
// ============================================================================

export const UpcomingEventOptionsSchema = z.object({
	type: z.literal("UPCOMING_EVENT"),
	daysBefore: z.number().int().min(1).max(30),
	sendAtTime: z.string().regex(/^\d{2}:\d{2}$/),
});

export const SameDayOptionsSchema = z.object({
	type: z.literal("SAME_DAY"),
	hoursBefore: z.number().min(0.5).max(24),
});

export const EventConfirmationOptionsSchema = z.object({
	type: z.literal("EVENT_CONFIRMATION"),
	delayMinutes: z.number().int().min(0).max(60),
});

export const CancellationNoticeOptionsSchema = z.object({
	type: z.literal("CANCELLATION_NOTICE"),
	delayMinutes: z.number().int().min(0).max(60),
});

export const EventModificationOptionsSchema = z.object({
	type: z.literal("EVENT_MODIFICATION"),
	delayMinutes: z.number().int().min(0).max(60),
});

export const PostEventFollowupOptionsSchema = z.object({
	type: z.literal("POST_EVENT_FOLLOWUP"),
	daysAfter: z.number().int().min(1).max(90),
	sendAtTime: z.string().regex(/^\d{2}:\d{2}$/),
});

export const NoShowFollowupOptionsSchema = z.object({
	type: z.literal("NO_SHOW_FOLLOWUP"),
	delayHours: z.number().min(0.5).max(168),
});

export const ReEngagementOptionsSchema = z.object({
	type: z.literal("RE_ENGAGEMENT"),
	// `inactiveDays` and `sendAtTime` are optional because the
	// `config_notification_rule` table currently lacks an `inactiveDays`
	// column, and `sendAtTime` is nullable. Consumers must default at the
	// read site (see app-config-db.mapper.ts: `notificationRuleOptionsFromDb`).
	inactiveDays: z.number().int().min(7).max(365).optional(),
	sendAtTime: z
		.string()
		.regex(/^\d{2}:\d{2}$/)
		.optional(),
});

export const CustomOptionsSchema = z.object({
	type: z.literal("CUSTOM"),
});

export const NotificationRuleOptionsSchema = z.discriminatedUnion("type", [
	UpcomingEventOptionsSchema,
	SameDayOptionsSchema,
	EventConfirmationOptionsSchema,
	CancellationNoticeOptionsSchema,
	EventModificationOptionsSchema,
	PostEventFollowupOptionsSchema,
	NoShowFollowupOptionsSchema,
	ReEngagementOptionsSchema,
	CustomOptionsSchema,
]);

export type NotificationRuleOptions = z.infer<
	typeof NotificationRuleOptionsSchema
>;

// ============================================================================
// Notification Rule Schema
// ============================================================================

export const NotificationRuleSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1).max(100),
	enabled: z.boolean(),
	options: NotificationRuleOptionsSchema,
	templateName: z.string().min(1),
	language: z.string().min(1),
	variableMapping: z.record(z.string(), z.string()),
	eventTypeFilter: z.array(z.string()).optional(),
	targetPhones: z.array(z.string()).optional(),
	/** Absolute ISO 8601 datetime for when the notification fires. When set, overrides relative timing in options. */
	scheduledAt: z.string().optional(),
	/** Delivery channels this rule targets. Optional; the DB defaults to WHATSAPP-only. */
	channels: z.array(NotificationChannelSchema).optional(),
});

export type NotificationRule = z.infer<typeof NotificationRuleSchema>;
